import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { isDashCacheBusy, onDashCacheBusy } from '../../utils/dash-fetch-cache';
import shared from '../../data/js/shared';
import type { RailItem } from '../side-rail';

/**
 * The dash's loading states, in one place, so a click always gets an answer.
 *
 * Owner, 2026-10-26: "dash needs to feel snappier when we change things, even
 * if it's just a loading spinner right away." Three shapes cover every case:
 *
 * - `DashRouteLoader`: over the content area while a rail click is loading the
 *   next page. AllLayout shows it; pages never do.
 * - `DashSpinner`: a panel's first load, before it has anything to show.
 * - `DashBusy`: a panel that has data and is fetching more (a filter, a tab, a
 *   date range, a revisit). The old data stays readable and a small
 *   "Refreshing" pill says new data is coming, instead of the panel blanking.
 */

/**
 * Held back 120ms (`.dash-spinner`, styles/components/side-rail.scss): a
 * revisited panel answers from the dash cache in a few ms, and the old inline
 * "Loading…" text painted for that one frame before the data replaced it.
 */
export function DashSpinner({
  label = 'Loading',
  className = '',
  testId = 'dash-spinner',
}: {
  label?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`dash-spinner flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400 ${className}`}
      data-testid={testId}
    >
      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function DashBusy({
  busy,
  children,
  label = 'Refreshing',
  className = '',
}: {
  busy: boolean;
  children: React.ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`} aria-busy={busy || undefined} data-testid="dash-busy">
      {busy && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-gray-900/80 px-2.5 py-1 text-xs font-semibold text-white shadow"
          data-testid="dash-busy-pill"
        >
          <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          {label}
        </div>
      )}
      <div className={busy ? 'opacity-60 transition-opacity' : 'transition-opacity'}>{children}</div>
    </div>
  );
}

/** The rail label for a dash path, so the loader can say where it is going. */
export function dashPageLabel(path: string): string | null {
  if (path === '/dash') return 'Overview';
  const items = shared.admin.navButtons as RailItem[];
  return items.find((i) => i.href === path || path.startsWith(`${i.href}/`))?.label ?? null;
}

export function DashRouteLoader({ to }: { to: string }) {
  const label = dashPageLabel(to);
  return (
    <div className="dash-route-loader" role="status" aria-live="polite" data-testid="dash-route-loader">
      <div className="dash-route-loader__bar" aria-hidden="true" />
      <div className="dash-route-loader__body">
        <Loader2 size={28} className="animate-spin" aria-hidden="true" />
        <span>{label ? `Loading ${label}…` : 'Loading…'}</span>
      </div>
    </div>
  );
}

/**
 * A small pill, bottom right, while a revisited page's data is being checked
 * behind the copy on screen. AllLayout shows it on every dash page.
 */
export function DashRefreshingPill() {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- adopts the cache's state at mount; it lives outside React
    setBusy(isDashCacheBusy());
    return onDashCacheBusy(setBusy);
  }, []);
  return <DashUpdatingPill show={busy} />;
}

/**
 * The same pill for a page's own reload: a filter, range or tab change on a
 * page that keeps its old numbers on screen until the new ones land.
 */
export function DashUpdatingPill({ show, label = 'Refreshing' }: { show: boolean; label?: string }) {
  if (!show) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="dash-scope pointer-events-none fixed bottom-4 right-4 z-20 flex items-center gap-1.5 rounded-full bg-gray-900/85 px-3 py-1.5 text-xs font-semibold text-white shadow-lg"
      data-testid="dash-refreshing-pill"
    >
      <Loader2 size={12} className="animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}
